import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { oidcService } from '../../services/oidcService';
import { fetchWorkspaceMemberships } from '../services/workspaceMembershipService';
import { setWorkspaceAuthenticated } from '../slices/authSlice';
import { WorkspaceMembership } from '../types/authTypes';
import {
  getAccessCredential,
  getWorkspacePreference,
} from '../utils/inMemoryTokenStore';

import { useAppDispatch } from '@store/hooks';

interface LocationState {
  returnTo?: string;
  userInitiated?: boolean;
}

const WorkspaceSelectorPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as LocationState)?.returnTo ?? '/';
  const userInitiated =
    (location.state as LocationState)?.userInitiated === true;

  const [memberships, setMemberships] = useState<WorkspaceMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      const token = getAccessCredential()?.value;
      if (!token) {
        setError('No access token available. Please sign in again.');
        setIsLoading(false);
        return;
      }

      try {
        const data = await fetchWorkspaceMemberships(token, controller.signal);
        setMemberships(data);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError('Failed to load workspaces. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (isLoading || memberships.length === 0 || autoSelectedRef.current)
      return;

    // Single membership: always auto-select regardless of how we got here
    if (memberships.length === 1) {
      autoSelectedRef.current = true;
      void handleSelect(memberships[0]);
      return;
    }

    // User deliberately navigated here to switch workspace — show the full list
    if (userInitiated) return;

    // System entry (session restore / AD-12 recovery): auto-select the stored preference
    const pref = getWorkspacePreference();
    if (pref) {
      const match = memberships.find(
        (m) => m.membershipId === pref.membershipId
      );
      if (match) {
        autoSelectedRef.current = true;
        void handleSelect(match);
      }
    }
  }, [isLoading, memberships, userInitiated]); // handleSelect is intentionally omitted — it would cause an infinite loop

  const handleSelect = async (membership: WorkspaceMembership) => {
    setIsSelecting(true);
    setError(null);

    try {
      const ctx = await oidcService.upgradeToWorkspaceToken(membership);
      dispatch(setWorkspaceAuthenticated(ctx));
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(
        (err as Error).message ??
          'Failed to activate workspace. Please try again.'
      );
      setIsSelecting(false);
    }
  };

  if (isLoading || isSelecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">
          {isSelecting ? 'Activating workspace...' : 'Loading workspaces...'}
        </p>
      </div>
    );
  }

  if (memberships.length === 0 && !error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h1 className="text-xl font-semibold text-gray-800">
          No Workspace Access
        </h1>
        <p className="text-gray-500">
          You do not have access to any workspaces. Contact your administrator.
        </p>
        <button
          className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
          onClick={() => void oidcService.logout()}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-6">
      <h1 className="text-2xl font-semibold text-gray-800">Select Workspace</h1>

      {error && (
        <div className="w-full max-w-md p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          <p>{error}</p>
          <div className="flex gap-3 mt-3">
            <button
              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-500"
              onClick={() => setError(null)}
            >
              Retry
            </button>
            <button
              className="px-3 py-1 border border-red-400 text-red-600 rounded text-sm hover:bg-red-50"
              onClick={() => void oidcService.logout()}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}

      <div className="w-full max-w-md flex flex-col gap-3">
        {memberships.map((m) => {
          const isActive =
            m.membershipId === getWorkspacePreference()?.membershipId;
          return (
            <button
              key={`${m.tenantId}-${m.companyId ?? 'tenant'}`}
              className={`w-full p-4 text-left border rounded-lg transition-colors ${
                isActive
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
              }`}
              onClick={() => void handleSelect(m)}
            >
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-900">
                  {m.companyName ?? m.tenantName}
                </p>
                {isActive && (
                  <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{m.tenantName}</p>
              {/* {m.roles.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {m.roles.join(', ')}
                </p>
              )} */}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceSelectorPage;
